// Admin client for /admin/advertisers — mirrors com.platform.marketing.controller.admin.AdminAdvertiserController.
// The list endpoint returns a plain array (NOT a PageResponse): the backend
// service has only a small handful of advertisers per tenant and the controller
// gates `includeArchived` as a query flag.
import { api } from "@/lib/api";

export interface AdvertiserDto {
    id: number;
    slug: string;
    name: string;
    contactEmail: string | null;
    billingCurrency: string | null;
    notes: string | null;
    status: string;
    archivedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateAdvertiserRequest {
    slug: string;
    name: string;
    contactEmail?: string | null;
    billingCurrency?: string | null;
    notes?: string | null;
}

export interface UpdateAdvertiserRequest {
    name?: string | null;
    contactEmail?: string | null;
    billingCurrency?: string | null;
    notes?: string | null;
    status?: string | null;
}

export interface AdvertiserDashboardDto {
    advertiserId: number;
    slug: string;
    name: string;
    status: string;
    domainsOwned: number;
    pagesOwned: number;
    draftsOwned: number;
    bannersOwned: number;
    aiInputTokens: number;
    aiOutputTokens: number;
    llmCostUsdMicros: number;
    domainSpendUsdMicros: number;
    imageGenCostUsdMicros: number;
    totalSpendUsdMicros: number;
}

export interface AssignmentLogEntryDto {
    id: number;
    entityType: string;
    entityId: number;
    oldOwnerId: number | null;
    newOwnerId: number | null;
    changedByAdminId: number | null;
    changedByEmail: string | null;
    changedAt: string;
    reason: string | null;
}

export async function listAdvertisers(includeArchived = false): Promise<AdvertiserDto[]> {
    const { data } = await api.get<AdvertiserDto[]>("/admin/advertisers", {
        params: { includeArchived },
    });
    return data;
}

export async function getAdvertiser(id: number): Promise<AdvertiserDto> {
    const { data } = await api.get<AdvertiserDto>(`/admin/advertisers/${id}`);
    return data;
}

export async function createAdvertiser(req: CreateAdvertiserRequest): Promise<AdvertiserDto> {
    const { data } = await api.post<AdvertiserDto>("/admin/advertisers", req);
    return data;
}

export async function updateAdvertiser(id: number, req: UpdateAdvertiserRequest): Promise<AdvertiserDto> {
    const { data } = await api.patch<AdvertiserDto>(`/admin/advertisers/${id}`, req);
    return data;
}

export async function archiveAdvertiser(id: number): Promise<void> {
    await api.post(`/admin/advertisers/${id}/archive`);
}

/** Hard-delete. Backend returns 409 if any FK refs exist (pages/drafts/domains/banners). */
export async function deleteAdvertiser(id: number): Promise<void> {
    await api.delete(`/admin/advertisers/${id}`);
}

export async function getAdvertiserDashboard(id: number): Promise<AdvertiserDashboardDto> {
    const { data } = await api.get<AdvertiserDashboardDto>(`/admin/advertisers/${id}/dashboard`);
    return data;
}

export async function getAdvertiserAssignmentLog(id: number, limit = 50): Promise<AssignmentLogEntryDto[]> {
    const { data } = await api.get<AssignmentLogEntryDto[]>(
        `/admin/advertisers/${id}/assignment-log`,
        { params: { limit } },
    );
    return data;
}

// --- Reassignment endpoints across the entity controllers.
// All five share the same body shape {advertiserId: number|null, reason?: string}.

export interface AssignAdvertiserBody {
    advertiserId: number | null;
    reason?: string | null;
}

export async function assignPageAdvertiser(pageId: number, body: AssignAdvertiserBody): Promise<void> {
    await api.patch(`/admin/pages/${pageId}/advertiser`, body);
}

export async function assignDomainAdvertiser(domainId: number, body: AssignAdvertiserBody): Promise<void> {
    await api.patch(`/admin/domains/${domainId}/advertiser`, body);
}

export async function assignBannerAdvertiser(bannerId: number, body: AssignAdvertiserBody): Promise<void> {
    await api.patch(`/admin/banners/${bannerId}/advertiser`, body);
}

export async function assignDraftAdvertiser(draftId: number, body: AssignAdvertiserBody): Promise<void> {
    await api.patch(`/admin/constructor/drafts/${draftId}/advertiser`, body);
}
